import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardHero, ArticleCardStandard, ArticleCardCompact, ArticleCardSidebar } from '../components/cards/ArticleCard';
import AuthModal from '../components/modals/AuthModal';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { TrendingUp, Sparkles, Clock, ChevronRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

export default function HomePage() {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    try {
      const [featuredRes, latestRes, trendingRes] = await Promise.all([
        axios.get(`${API}/articles/featured`, { params: { limit: 5 } }),
        axios.get(`${API}/articles`, { params: { limit: 12 } }),
        axios.get(`${API}/articles/trending`, { params: { limit: 5 } }),
      ]);

      setFeaturedArticles(featuredRes.data);
      setLatestArticles(latestRes.data);
      setTrendingArticles(trendingRes.data);

      const categoryData = {};
      for (const cat of categories.slice(0, 4)) {
        try {
          const res = await axios.get(`${API}/articles`, { params: { category: cat.slug, limit: 4 } });
          categoryData[cat.slug] = res.data;
        } catch (e) {
          categoryData[cat.slug] = [];
        }
      }
      setCategoryArticles(categoryData);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="home-page">
      <StockTicker />
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section py-6 sm:py-10" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
              <div className="lg:col-span-8">
                <Skeleton className="w-full h-[280px] sm:h-[400px] lg:h-[500px] rounded-none" />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
                <Skeleton className="w-full h-[140px] sm:h-[180px] lg:h-[240px] rounded-none" />
                <Skeleton className="w-full h-[140px] sm:h-[180px] lg:h-[240px] rounded-none" />
              </div>
            </div>
          ) : featuredArticles.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
              <div className="lg:col-span-8">
                <ArticleCardHero article={featuredArticles[0] || latestArticles[0]} />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
                {(featuredArticles.slice(1, 3).length > 0 ? featuredArticles.slice(1, 3) : latestArticles.slice(1, 3)).map((article) => (
                  <ArticleCardHero key={article.id} article={article} />
                ))}
              </div>
            </div>
          ) : latestArticles.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
              <div className="lg:col-span-8">
                <ArticleCardHero article={latestArticles[0]} />
              </div>
              <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
                {latestArticles.slice(1, 3).map((article) => (
                  <ArticleCardHero key={article.id} article={article} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-[#64748B]" style={{ fontFamily: "'Outfit', sans-serif" }}>No articles available yet. Content is being fetched...</p>
            </div>
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="divider-gold" />
      </div>

      {/* Main Content */}
      <section className="py-8 sm:py-16" data-testid="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12">
            {/* Articles Grid */}
            <div className="lg:col-span-8">
              {/* Latest News */}
              <div className="mb-10 sm:mb-16">
                <div className="flex items-center justify-between mb-6 sm:mb-8 pl-4 border-l-[3px] border-[#C5A059]">
                  <h2 className="flex items-center text-lg sm:text-2xl text-[#0A192F]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                    <Clock className="w-4 sm:w-5 h-4 sm:h-5 mr-3 text-[#C5A059]" strokeWidth={1.5} />
                    Latest News
                  </h2>
                </div>
                {loading ? (
                  <div className="space-y-5">
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[140px] sm:h-[180px] rounded-none" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-5">
                    {latestArticles.slice(0, 6).map((article) => (
                      <ArticleCardStandard key={article.id} article={article} />
                    ))}
                  </div>
                )}
              </div>

              {/* Category Sections */}
              {categories.slice(0, 3).map((cat) => (
                <div key={cat.slug} className="mb-10 sm:mb-16">
                  <div className="flex items-center justify-between mb-6 sm:mb-8 pl-4 border-l-[3px] border-[#C5A059]">
                    <h2 className="text-base sm:text-xl text-[#0A192F]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                      {cat.name}
                    </h2>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="flex items-center text-xs uppercase tracking-widest text-[#C5A059] hover:text-[#a8853d] transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                    </Link>
                  </div>
                  {loading ? (
                    <div className="space-y-5">
                      {[...Array(2)].map((_, i) => (
                        <Skeleton key={i} className="w-full h-[140px] sm:h-[180px] rounded-none" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {(categoryArticles[cat.slug] || latestArticles.filter(a => a.category === cat.slug)).slice(0, 4).map((article) => (
                        <ArticleCardStandard key={article.id} article={article} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-8 sm:space-y-10">
              {/* Newsletter Widget */}
              <div className="sidebar-widget p-5 sm:p-6 rounded-none">
                <h3 className="text-base sm:text-lg text-[#0A192F] mb-3 sm:mb-4" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Stay Updated
                </h3>
                <p className="text-[#64748B] text-xs sm:text-sm mb-3 sm:mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Get the latest financial news and insights delivered to your inbox.
                </p>
                <Button
                  onClick={() => setAuthModalOpen(true)}
                  className="w-full bg-[#0A192F] text-white hover:bg-[#0A192F]/90 uppercase tracking-widest text-xs rounded-none"
                  data-testid="subscribe-btn"
                >
                  <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Subscribe
                </Button>
              </div>

              {/* Trending */}
              <div className="sidebar-widget p-5 sm:p-6 rounded-none">
                <h3 className="text-base sm:text-lg text-[#0A192F] mb-3 sm:mb-4 flex items-center" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  <TrendingUp className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                  Trending
                </h3>
                <div className="space-y-0">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[60px] mb-4 rounded-none" />
                    ))
                  ) : trendingArticles.length > 0 ? (
                    trendingArticles.map((article, index) => (
                      <ArticleCardCompact key={article.id} article={article} index={index} />
                    ))
                  ) : latestArticles.slice(0, 5).map((article, index) => (
                    <ArticleCardCompact key={article.id} article={article} index={index} />
                  ))}
                </div>
              </div>

              {/* Editor's Picks */}
              <div className="sidebar-widget p-5 sm:p-6 rounded-none">
                <h3 className="text-base sm:text-lg text-[#0A192F] mb-3 sm:mb-4 flex items-center" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-[#C5A059]" strokeWidth={1.5} />
                  Editor's Picks
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[70px] sm:h-[80px] rounded-none" />
                    ))
                  ) : (featuredArticles.length > 0 ? featuredArticles : latestArticles).slice(0, 5).map((article) => (
                    <ArticleCardSidebar key={article.id} article={article} />
                  ))}
                </div>
              </div>

              {/* Advertise CTA */}
              <div className="sidebar-widget p-5 sm:p-6 rounded-none bg-[#0A192F] border-[#0A192F]">
                <h3 className="text-base sm:text-lg text-white mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Advertise With Us
                </h3>
                <p className="text-white/60 text-xs sm:text-sm mb-3 sm:mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Reach thousands of finance-focused readers with your message.
                </p>
                <Link
                  to="/advertise"
                  className="inline-block bg-[#C5A059] text-[#0A192F] px-5 py-2 text-xs font-semibold uppercase tracking-widest rounded-none hover:bg-[#d4b972] transition-colors"
                  data-testid="advertise-cta"
                >
                  Learn More
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode="register" />
    </div>
  );
}
